import api from './api';

// Types
export interface Currency {
  id: number;
  code: string;
  rate: number;
  created_at: string;
  updated_at: string;
}

export interface Wallet {
  id: number;
  user_id: number;
  currency_code: string;
  balance: string;
  is_active: boolean;
  currency: Currency;
  created_at: string;
  updated_at: string;
}

export interface Transaction {
  id: number;
  wallet_id: number;
  payment_method_id: number | null;
  type: 'deposit' | 'withdrawal' | 'transfer';
  amount: string;
  fee: string;
  currency_code: string;
  status: 'pending' | 'completed' | 'failed';
  reference_id: string | null;
  reference_type: string | null;
  description: string;
  created_at: string;
  updated_at: string;
}

export interface WalletResponse {
  success: boolean;
  data: Wallet;
}

export interface WalletsResponse {
  success: boolean;
  data: {
    data: Wallet[];
  };
}

export interface TransactionsResponse {
  success: boolean;
  data: {
    data: Transaction[];
  };
}

export interface AddFundsResponse {
  success: boolean;
  data: {
    wallet: Wallet;
    transaction: Transaction;
  };
}

export interface WithdrawFundsResponse {
  success: boolean;
  data: {
    wallet: Wallet;
    transaction: Transaction;
  };
}

// Functions
const getWallets = async (): Promise<WalletsResponse> => {
  const response = await api.get('/wallets');
  return response.data;
};

const getWallet = async (walletId: number): Promise<WalletResponse> => {
  const response = await api.get(`/wallets/${walletId}`);
  return response.data;
};

const addFunds = async (data: {
  amount: number;
  currency_code: string;
  payment_method_id: number;
  metadata?: Record<string, any>;
}): Promise<AddFundsResponse> => {
  const response = await api.post('/wallets/add-funds', data);
  return response.data;
};

const withdrawFunds = async (data: {
  wallet_id: number;
  amount: number;
  metadata?: Record<string, any>;
}): Promise<WithdrawFundsResponse> => {
  const response = await api.post('/wallets/withdraw', data);
  return response.data;
};

const getWalletTransactions = async (walletId: number): Promise<TransactionsResponse> => {
  const response = await api.get(`/wallets/${walletId}/transactions`);
  return response.data;
};

const downloadTransactionReceipt = async (transactionId: string, format: 'pdf' | 'xlsx' = 'xlsx'): Promise<Blob> => {
  const response = await api.get(`/wallets/transactions/${transactionId}/receipt/download`, {
    params: { format },
    responseType: 'blob'
  });
  return response.data;
};

const walletService = {
  getWallets,
  getWallet,
  addFunds,
  withdrawFunds,
  getWalletTransactions,
  downloadTransactionReceipt,
};

export default walletService; 